<template>
  <div class="appointment-report ar-rating">
    <fieldset
      class="ar-rating__group"
      :aria-describedby="scoreInvalid ? scoreErrorId : null"
    >
      <legend class="ar-rating__label">
        {{ STANDARD_COPY.score }}
      </legend>
      <div
        class="ar-rating__stars"
        :class="{
          'ar-rating__stars--invalid': scoreInvalid,
          'ar-rating__stars--inert': readonly,
        }"
        @mouseleave="hovered = 0"
      >
        <label
          v-for="value in SPECIALIST_SCORES"
          :key="value"
          class="ar-rating__star"
          :class="{ 'ar-rating__star--on': value <= shownScore }"
          @mouseenter="onHover(value)"
        >
          <input
            :id="`${uid}-star-${value}`"
            ref="stars"
            type="radio"
            class="ar-sr-only"
            :name="`${uid}-score`"
            :value="value"
            :checked="score === value"
            :disabled="readonly"
            @click="onStarClick(value)"
          >
          <span
            class="ar-rating__star-face"
            aria-hidden="true"
          >
            <feather-icon
              icon="StarIcon"
              size="28"
            />
          </span>
          <span class="ar-sr-only">{{ value }}</span>
        </label>
      </div>
      <span
        v-if="scoreInvalid"
        :id="scoreErrorId"
        class="ar-rating__error ar-anim-fade-in"
      >{{ scoreError }}</span>
    </fieldset>

    <!-- Destaques da nota (a lista muda com a nota — a mesma consulta do componente de estrelas da sidebar) -->
    <transition name="ar-reveal">
      <div
        v-if="score"
        class="ar-rating__highlights"
      >
        <div class="ar-rating__highlights-inner">
          <p
            v-if="loading"
            class="ar-rating__status"
            role="status"
          >
            <svg
              class="ar-spinner"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-opacity=".25"
                stroke-width="2.5"
              />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
              />
            </svg>
            {{ STANDARD_COPY.highlightsLoading }}
          </p>

          <template v-else-if="options.length">
            <p
              :id="highlightsLabelId"
              class="ar-rating__label"
            >
              {{ STANDARD_COPY.highlights(max) }}
            </p>
            <div
              role="group"
              class="ar-rating__chips"
              :aria-labelledby="highlightsLabelId"
              :aria-describedby="highlightsInvalid ? highlightsErrorId : countId"
            >
              <button
                v-for="name in options"
                :key="name"
                ref="chips"
                type="button"
                class="ar-rating__chip"
                :class="{
                  'ar-rating__chip--selected': isSelected(name),
                  'ar-rating__chip--full': !isSelected(name) && isFull,
                  'ar-rating__chip--invalid': highlightsInvalid,
                }"
                :aria-pressed="isSelected(name) ? 'true' : 'false'"
                :aria-disabled="!isSelected(name) && isFull ? 'true' : null"
                @click="toggle(name)"
              >
                <span
                  class="ar-rating__chip-check"
                  aria-hidden="true"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="14"
                  />
                </span>
                {{ name }}
              </button>
            </div>
            <span
              :id="countId"
              class="ar-rating__count ar-tabular"
            >{{ STANDARD_COPY.highlightsCount(highlights.length, max) }}</span>
            <span
              v-if="highlightsInvalid"
              :id="highlightsErrorId"
              class="ar-rating__error ar-anim-fade-in"
            >{{ highlightsError }}</span>
          </template>

          <p
            v-else
            class="ar-rating__status"
          >
            {{ STANDARD_COPY.highlightsEmpty }}
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { SPECIALIST_SCORES, STANDARD_COPY } from '@/constants/appointment_report_standard'
import { nextReportUid } from '../helpers/report_ui'

/**
 * "Dê uma nota de 1 a 5" + "O que destacou na avaliação?" do report dos tipos padrão, no visual novo — substitui o
 * antigo `StarRating.vue` da sidebar (removido junto com ela) com as mesmas regras:
 *  - cinco estrelas num grupo de rádios reais (Tab entra, setas trocam a nota); clicar na nota marcada a desmarca,
 *    como na sidebar (a nota é opcional na Entrevista);
 *  - com nota, os destaques daquela nota (carregados pelo formulário, `options` = nomes); até `max` (4), os demais
 *    ficam indisponíveis ao chegar no limite;
 *  - trocar a nota limpa os destaques (quem limpa é o formulário, que também recarrega a lista).
 *
 * Presentacional: o estado (nota, destaques, lista, carregando, erros) é do `standardReportForm`. Eventos
 * `update:score` (1–5 ou null) e `update:highlights` (lista de nomes). `focus()` vai para a nota;
 * `focusHighlights()`, para o primeiro destaque.
 */
export default {
  name: 'ReportSpecialistRating',
  props: {
    score: {
      type: Number,
      default: null,
    },
    highlights: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    max: {
      type: Number,
      default: 4,
    },
    scoreInvalid: {
      type: Boolean,
      default: false,
    },
    scoreError: {
      type: String,
      default: '',
    },
    highlightsInvalid: {
      type: Boolean,
      default: false,
    },
    highlightsError: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    idPrefix: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      STANDARD_COPY,
      SPECIALIST_SCORES,
      fallbackUid: nextReportUid('ar-rating'),
      hovered: 0,
    }
  },
  computed: {
    uid() {
      return this.idPrefix || this.fallbackUid
    },
    shownScore() {
      return this.hovered || this.score || 0
    },
    isFull() {
      return this.highlights.length >= this.max
    },
    scoreErrorId() {
      return `${this.uid}-score-error`
    },
    highlightsLabelId() {
      return `${this.uid}-highlights-label`
    },
    highlightsErrorId() {
      return `${this.uid}-highlights-error`
    },
    countId() {
      return `${this.uid}-highlights-count`
    },
  },
  methods: {
    onHover(value) {
      if (!this.readonly) this.hovered = value
    },
    // O `click` do rádio também dispara na navegação por setas (com a nova nota) e no Espaço sobre a nota marcada.
    onStarClick(value) {
      if (this.readonly) return
      this.$emit('update:score', value === this.score ? null : value)
    },
    isSelected(name) {
      return this.highlights.includes(name)
    },
    toggle(name) {
      if (this.readonly) return
      if (this.isSelected(name)) {
        this.$emit('update:highlights', this.highlights.filter(item => item !== name))
        return
      }
      if (this.isFull) return
      this.$emit('update:highlights', [...this.highlights, name])
    },
    focus(options) {
      const stars = this.$refs.stars || []
      const index = this.score ? this.score - 1 : 0
      const target = stars[index] || stars[0]
      if (target) target.focus(options)
    },
    focusHighlights(options) {
      const chips = this.$refs.chips || []
      if (chips[0]) {
        chips[0].focus(options)
        return
      }
      this.focus(options)
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-rating {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ar-rating__group {
  min-width: 0;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 0;
}

.ar-rating__label {
  float: none;
  width: auto;
  margin: 0;
  padding: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ar-text-1);
}

// ---- Estrelas (32×32, 8px de intervalo) ----
.ar-rating__stars {
  align-self: flex-start;
  display: flex;
  gap: 8px;
}

.ar-rating__star {
  position: relative;
  margin: 0;
  display: flex;
  cursor: pointer;
}

.ar-rating__stars--inert .ar-rating__star {
  cursor: default;
}

.ar-rating__star-face {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--ar-radius-control);
  color: var(--ar-control);
  transition:
    color var(--ar-dur-hover) var(--ar-ease-standard),
    transform var(--ar-dur-press) var(--ar-ease-standard);

  ::v-deep svg {
    fill: transparent;
    transition: fill var(--ar-dur-hover) var(--ar-ease-standard);
  }
}

.ar-rating__star--on .ar-rating__star-face {
  color: var(--ar-brand-text);

  ::v-deep svg {
    fill: currentColor;
  }
}

.ar-rating__stars--invalid .ar-rating__star:not(.ar-rating__star--on) .ar-rating__star-face {
  color: var(--ar-warning);
}

.ar-rating__stars:not(.ar-rating__stars--inert) .ar-rating__star:active .ar-rating__star-face {
  transform: scale(.94);
}

.ar-rating__star input:focus-visible + .ar-rating__star-face {
  outline: 2px solid var(--ar-focus);
  outline-offset: 1px;
}

.ar-rating__error {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-warning);
}

// ---- Destaques ----
.ar-rating__highlights-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ar-rating__status {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-muted);
}

.ar-rating__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ar-rating__chip {
  min-height: 32px;
  margin: 0;
  padding: 5px 12px;
  display: inline-flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--ar-control);
  border-radius: var(--ar-radius-pill);
  background: var(--ar-bg-field);
  font-family: inherit;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
  text-align: left;
  cursor: pointer;
  transition:
    background-color var(--ar-dur-hover) var(--ar-ease-standard),
    border-color var(--ar-dur-hover) var(--ar-ease-standard),
    color var(--ar-dur-hover) var(--ar-ease-standard),
    gap var(--ar-dur-fast) var(--ar-ease-standard);

  &:hover:not([aria-disabled='true']) {
    border-color: var(--ar-control-hover);
    background: var(--ar-bg-field-hover);
    color: var(--ar-text-1);
  }

  &:focus-visible {
    outline: 2px solid var(--ar-focus);
    outline-offset: 2px;
  }
}

// O check entra alargando de 0 a 14px (como os chips do Novo compromisso).
.ar-rating__chip-check {
  width: 0;
  display: inline-flex;
  overflow: hidden;
  color: var(--ar-brand-text);
  transition: width var(--ar-dur-fast) var(--ar-ease-standard);
}

.ar-rating__chip--selected,
.ar-rating__chip--selected:hover:not([aria-disabled='true']) {
  gap: 6px;
  border-color: var(--ar-brand-text);
  background: var(--ar-selected-tint);
  color: var(--ar-text-1);

  .ar-rating__chip-check {
    width: 14px;
  }
}

.ar-rating__chip--invalid:not(.ar-rating__chip--selected) {
  border-color: var(--ar-warning);
}

.ar-rating__chip--full {
  opacity: .56;
  cursor: not-allowed;
}

.ar-rating__count {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ar-text-muted);
}
</style>
